<template>
    <ai-process-modal
        mode="generate"
        :node-type="nodeType || 'process'"
        :create-mode="true"
        :busy="creating"
        :inline="inline"
        :from-slug="fromSlug || null"
        :initial-note="initialNote || null"
        :initial-method="initialMethod || null"
        ref="modal"
        @close="$emit('close')"
        @accept="onAccept"
    />
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import AIProcessModal from '@/components/AIProcessModal.vue';
import { createNodeFromDraft, EDIT_ROUTES, AINodeType } from '@/ai/createNodeFromDraft';

// "Generate with AI" quick-create. Hosts the AI modal in create mode — describe /
// record / upload first, then the review step shows the draft together with
// the details (owner, locations, departments, types, link) pre-filled by the
// AI. Accept creates the node and opens its editor; no trip through the manual
// Add form. Drop-in for any page bar:
//   <ai-create-node v-if="show" :node-type="'process'" :from-slug="node.Slug" @close="show = false" />
export default defineComponent({
    name: 'AICreateNode',
    // nodeType: 'process' (default) | 'checklist' | 'sheet' | 'timeline' — the starting selection
    // in the modal's type picker; fromSlug links the new node under a playbook tree node.
    // inline: render as a card on the page instead of a modal (The Forge page)
    // initialMethod: 'record' | 'upload' opens that capture card first (the Forge page's Record menu)
    props: ['nodeType', 'fromSlug', 'inline', 'initialNote', 'initialMethod'],
    components: { AiProcessModal: AIProcessModal },
    emits: ['close', 'created'],
    data() {
        return { creating: false };
    },
    methods: {
        // The Forge page's "All tools": same checks as the card's × (recording in progress, unsaved draft)
        requestClose() { return (this.$refs.modal as any)?.requestClose(); },
        async onAccept(draft: any, jobId: any, meta: any) {
            if (this.creating) return;
            this.creating = true;
            const nodeType: AINodeType = (meta && meta.nodeType) || this.nodeType || 'process';
            try {
                if (meta && meta.extend) {
                    // Add-to-existing: the merged draft is saved onto the node as a draft awaiting approval
                    const targetSlug = meta.targetSlug || draft.targetSlug;
                    await axios.post(`${this.$store.state.apiUrl}/api/ai/extend/apply`, { slug: targetSlug, nodeType, draft: { ...draft, jobId: jobId || draft.jobId || null } });
                    this.$store.dispatch('setStoreData');
                    this.$emit('created', targetSlug);
                    this.$router.push(EDIT_ROUTES[nodeType] + targetSlug);
                    this.$emit('close');
                    return;
                }
                const slug = await createNodeFromDraft({
                    apiUrl: this.$store.state.apiUrl,
                    nodeType,
                    draft,
                    meta,
                    fromSlug: this.fromSlug || null,
                    jobId
                });
                this.$store.dispatch('setStoreData');
                this.$emit('created', slug);
                this.$router.push(EDIT_ROUTES[nodeType] + slug);
                this.$emit('close');
            } catch (e: any) {
                if (e?.status === 403 || e?.response?.status === 403) {
                    this.$router.push('/login?redirect=' + encodeURIComponent(this.$route.fullPath));
                    return;
                }
                alert(e?.response?.data || e?.message || 'There was an error creating it — please try again');
            } finally {
                this.creating = false;
            }
        }
    }
});
</script>
